import React, { useEffect, useState } from 'react';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useUpdateEffect } from '@shared/utils/hooks/useUpdateEffect';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useEventVenueDialogTelemetry } from './EventVenueDialog.telemetry';
import { useEventPlaces } from '@shared/utils/eventPlace';
import type { EventVenue } from '@shared/utils/eventPlace';
import { addError } from '@shared/utils/logger';
import { CloseButton, DialogV2, DrawerV2, Flex } from '@withjoy/joykit';
import type { DialogV2Props } from '@withjoy/joykit';
import { JoyInteractableMapProvider } from '@shared/components/JoyInteractableMap/JoyInteractableMap.provider';
import { Map } from './EventVenueDialog.Map';
import { dialogOverridesV2, drawerOverrides } from '@shared/components/ResponsiveDialog/ResponsiveDialog.styles';
import { Form } from '../Form';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { VirtualScrollableArea } from '@website-editor/utils/WebsiteEditor.utils';
import { VenueError } from '../VenueError';
import { useBestAnchorLocation } from '@apps/stays/AccommodationsConsole/hooks/useBestAnchorLocation';
import { getValidCoordinates } from '@shared/core/places';
import { useTranslation } from '@shared/core';
import { useEventPhoto } from '@shared/utils/eventPhoto';
import type { AdvancedLocationInputV2FormFields } from '@shared/components/LocationInputV2';
import { useUserConsent } from '@shared/utils/userConsent';
import { ACCOMMODATIONS_DEFAULT_LOCATION } from '@shared/constants/accommodations';
import type { BestAnchorLocation, EventVenueDialogProps, EventVenueState } from './EventVenueDialog.types';
import type { LatLngLiteral } from '@shared/core/google-maps/types';

const DEFAULT_DESTINATION: LatLngLiteral = {
  lat: ACCOMMODATIONS_DEFAULT_LOCATION.lat,
  lng: ACCOMMODATIONS_DEFAULT_LOCATION.long,
};

const desktopDialogOverrides: DialogV2Props['overrides'] = {
  ...dialogOverridesV2,
  Backdrop: {
    props: {
      background: 'rgba(0, 0, 0, 0.60)',
      backdropFilter: 'blur(25px)',
    },
  },
  Content: {
    props: {
      overflow: 'hidden' as const,
    },
  },
} as const;

export const EventVenueDialog = (props: EventVenueDialogProps) => {
  const { eventVenueState: eventVenueStateArg, isOpen, onClose, pageSlug, isSuperAdmin, onSaveVenue } = props;

  const telemetry = useEventVenueDialogTelemetry();
  const { eventInfo, eventHandle } = useEventInfo();
  const isMobile = useIsMobileScreen();
  const { tq } = useTranslation('accommodationsAdmin');

  const [eventVenueState, setEventVenueState] = useState<EventVenueState>(eventVenueStateArg);
  const [newVenueInfo, setNewVenueInfo] = useState<EventVenue>();
  const [bestAnchorLocation, setBestAnchorLocation] = useState<BestAnchorLocation | null>(null);

  const { venueInfo } = useEventPlaces();
  const { eventPhoto } = useEventPhoto(eventHandle, !eventHandle);

  const venue = newVenueInfo?.googlePlaceId ? newVenueInfo : venueInfo;
  const venueCoords = getValidCoordinates(venue);
  const locationInfoCoords = getValidCoordinates(eventInfo?.locationInfo);

  useBestAnchorLocation({ onCompleted: setBestAnchorLocation, skip: !!venueCoords || !!locationInfoCoords });

  const { getUserConsent, setUserConsent } = useUserConsent();

  const bestAnchorCoords = bestAnchorLocation
    ? getValidCoordinates({ latitude: parseFloat(bestAnchorLocation.latitude), longitude: parseFloat(bestAnchorLocation.longitude) })
    : undefined;

  const destination = venueCoords ?? locationInfoCoords ?? bestAnchorCoords ?? DEFAULT_DESTINATION;

  useUpdateEffect(() => {
    if (isOpen) {
      setEventVenueState(eventVenueStateArg);
      setNewVenueInfo(undefined);
    }
  }, [isOpen, eventVenueStateArg]);

  const trackDialogOpen = useEventCallback(() => {
    if (isOpen) telemetry.eventVenueDialog.enter(eventVenueState);
  });
  useEffect(() => {
    trackDialogOpen();
  }, [isOpen, trackDialogOpen]);

  const handleOnSaveVenue = useEventCallback((location?: AdvancedLocationInputV2FormFields) => {
    if (!location) return;

    // Reset accommodations tour consent key if user has seen the tour, to show the tour again after changing the venue
    if (getUserConsent('hasSeenAccommodationsTour')) {
      telemetry.resetHasSeenAccommodationsTour();
      setUserConsent('hasSeenAccommodationsTour', false);
    }

    onSaveVenue(location);
    telemetry.eventVenueDialogStateChanged('update', eventHandle);
    setEventVenueState('update');
    onClose();
  });

  const failedToSaveVenue = useEventCallback(() => {
    addError('Error saving event venue', { context: 'EventVenueDialog', eventHandle });
    telemetry.eventVenueDialogStateChanged('error', eventHandle);
    setEventVenueState('error');
  });

  const handleDialogDismiss = useEventCallback(() => {
    telemetry.eventVenueDialogStateChanged('create', eventHandle);
    setEventVenueState('create');
    onClose();
  });

  const handleOnChangeVenue = useEventCallback((location?: AdvancedLocationInputV2FormFields) => {
    if (location?.locationValue) {
      setNewVenueInfo({
        googlePlaceId: location.placeId,
        displayName: location.locationValue,
        name: location.locationValue,
        latitude: location.latitude,
        longitude: location.longitude,
        address: location.address,
      });
    } else {
      setNewVenueInfo(undefined);
    }
  });

  const handleOnClose = useEventCallback(() => {
    telemetry.closeEventVenueDialog(eventVenueState, eventHandle);
    onClose();
  });

  const content =
    eventVenueState === 'error' ? (
      <VenueError />
    ) : (
      <Form
        eventVenueState={eventVenueState}
        venueInfo={venueInfo}
        onSaveVenue={handleOnSaveVenue}
        onError={failedToSaveVenue}
        eventHandle={eventHandle}
        pageSlug={pageSlug}
        isSuperAdmin={isSuperAdmin}
        handleDialogDismiss={handleDialogDismiss}
        onChangeVenue={handleOnChangeVenue}
      />
    );

  const mapPanel = (
    <Flex
      flex={{ sm2: 1 }}
      height={{ _: '100%', sm2: 'auto' }}
      width={'100%'}
    >
      <Map
        destination={destination}
        eventPhoto={eventPhoto?.url}
      />
    </Flex>
  );

  const dialogContent = (
    <JoyInteractableMapProvider>
      <CloseButton
        shape="rounded"
        size="lg"
        position="absolute"
        top={4}
        right={4}
        zIndex="popover"
        background="white"
        onClick={handleOnClose}
        aria-label={tq`Close`}
        id={'dialog-close'}
      />
      <Flex
        width="100%"
        height="100%"
      >
        {isMobile ? (
          content
        ) : (
          <Flex
            width="100%"
            height="auto"
            flexDirection={{ _: 'column-reverse', sm2: 'row' }}
          >
            <Flex
              position="relative"
              justifyContent="center"
              alignItems="center"
              flex={1}
            >
              <VirtualScrollableArea
                width={'100%'}
                overflowY="auto"
                display={'flex'}
                height={{ __: 'auto', sm2: '83vh' }}
                flexDirection="column"
                justifyContent={'space-between'}
                alignItems={'center'}
                gap={7}
                paddingY={{ __: 5, sm2: 8 }}
              >
                {content}
              </VirtualScrollableArea>
            </Flex>
            {mapPanel}
          </Flex>
        )}
      </Flex>
    </JoyInteractableMapProvider>
  );

  if (isMobile) {
    return (
      <DrawerV2
        isOpen={isOpen}
        overrides={drawerOverrides}
        onClose={handleOnClose}
        scrollBehavior="inside"
        disableFocusLockRemoveScroll
        disableAutoFocus
        anchor="bottom"
      >
        {dialogContent}
      </DrawerV2>
    );
  }

  return (
    <DialogV2
      isOpen={isOpen}
      onClose={handleOnClose}
      scrollBehavior="inside"
      size="5xl"
      disableAutoFocus
      disableFocusLockRemoveScroll
      overrides={desktopDialogOverrides}
    >
      {dialogContent}
    </DialogV2>
  );
};
